import { useCallback, useEffect, useMemo, useState } from 'react';
import { SortingState, Updater } from '@tanstack/react-table';
import { Link } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-hot-toast';
import { formatCurrency } from '~/common/form/Inputs';
import {
    AllRefundsFilters,
    CampaignRefundRow,
    RefundTotals,
    fetchAllRefunds,
} from '~/services/campaign';
import { fetchRefundDonation, markRefundComplete } from '~/services/refund';
import { DonationDetail } from './drawers/DonationDetailDrawer';
import RefundsTable, { REFUNDS_PAGE_SIZE } from './RefundsTable';

type Props = {
    filters: AllRefundsFilters;
    /**
     * Hands the fetched donation to the page's Donation Detail drawer. The
     * drawer is rendered once at page level and shared with the contributions
     * view, so the row can only supply the record, not open it.
     */
    onOpenDonation?: (donation: DonationDetail) => void;
};

/**
 * The "Refunds" view of the One Time Contributions list (HHLTB-2630).
 *
 * Refunds can't be read off the contributions list itself — a full refund
 * deletes the donation row and a partial one leaves an amount that is already
 * net — so this reads the refund records directly, across every campaign, at
 * every status. Same data as the Refund Audit Trail report, minus the report's
 * column picker and CSV; the link to it stays in the summary line for anyone
 * who needs the export.
 *
 * The RE/MIP flag is togglable from here too: this is where finance ends up when
 * reviewing what came back, so making them open the report or hunt down the
 * payout just to record the entry was the odd part.
 */
type RefundsResponse = {
    count: number;
    results: CampaignRefundRow[];
    totals: RefundTotals;
};

const AllRefundsTable: React.FC<Props> = ({ filters, onOpenDonation }) => {
    const [page, setPage] = useState<number>(1);
    const [sorting, setSorting] = useState<SortingState>([]);
    const queryClient = useQueryClient();
    // In-flight RE/MIP toggle, keyed by refund id
    const [savingRefundId, setSavingRefundId] = useState<number | null>(null);
    // Refund whose donation is being fetched for the drawer
    const [openingRefundId, setOpeningRefundId] = useState<number | null>(null);

    // Filters are owned by the page, so a change there has to send us back to
    // page 1 — otherwise a narrower filter lands on a page that no longer exists.
    useEffect(() => {
        setPage(1);
    }, [
        filters.from,
        filters.to,
        filters.search,
        filters.ntaf_id,
        filters.status,
    ]);

    // Sorting is the server's job so it covers every refund in the filtered
    // set, not the twenty rows on the page in hand (HHLTB-2654).
    const sortParams = useMemo(() => {
        const [first] = sorting;
        if (!first) return {};
        return { sort_by: first.id, sort_dir: first.desc ? 'desc' : 'asc' };
    }, [sorting]);

    // A new sort reorders the whole result set, so the old page number is
    // meaningless — go back to the first page of the new order.
    const handleSortingChange = (updater: Updater<SortingState>) => {
        setPage(1);
        setSorting(updater);
    };

    const query = useMemo(
        () => ({ ...filters, ...sortParams }),
        [filters, sortParams]
    );

    const { data, isLoading, isError } = useQuery({
        queryKey: ['all-refunds', page, query],
        queryFn: () => fetchAllRefunds(page, query),
        staleTime: Infinity,
    });

    // Refunds aren't auto-linked to RE/MIP, so finance staff flip this flag by
    // hand once the entry is made (HHLTB-2586). Same action as the Payouts view
    // and the refund audit report, offered here so the refunds a reviewer is
    // already looking at can be cleared without leaving the list.
    const handleToggleCompletion = useCallback(
        async (refundId: number, next: 'complete' | 'pending') => {
            setSavingRefundId(refundId);
            try {
                const updated = await markRefundComplete(refundId, next);
                // Patch the row in place rather than invalidating: the response
                // already carries the new state, and a refetch under a
                // completion filter would drop the row the instant it is
                // clicked, pulling it out from under the reviewer mid-pass.
                queryClient.setQueryData<RefundsResponse>(
                    ['all-refunds', page, query],
                    (prev) =>
                        prev
                            ? {
                                  ...prev,
                                  results: prev.results.map((row) =>
                                      row.refund_id === refundId
                                          ? {
                                                ...row,
                                                completion_status:
                                                    updated.completion_status,
                                                completed_at:
                                                    updated.completed_at,
                                                completed_by:
                                                    updated.completion_status ===
                                                    'complete'
                                                        ? updated.completion_changed_by
                                                        : '',
                                                completion_changed_by:
                                                    updated.completion_changed_by,
                                                completion_changed_at:
                                                    updated.completion_changed_at,
                                            }
                                          : row
                                  ),
                              }
                            : prev
                );
                toast.success(
                    next === 'complete'
                        ? 'Refund marked complete'
                        : 'Refund reopened'
                );
            } catch (e) {
                console.error('Failed to update refund completion status:', e);
                toast.error(
                    e instanceof Error
                        ? e.message
                        : 'Failed to update refund status'
                );
            } finally {
                setSavingRefundId(null);
            }
        },
        [queryClient, page, query]
    );

    // Fetched by refund rather than by donation id: a full refund deletes the
    // donation row, so for those rows the endpoint rebuilds the record from the
    // refund and the Stripe charge. Every row can open the drawer either way.
    const handleOpenDonation = useCallback(
        async (refundId: number) => {
            if (!onOpenDonation) return;
            setOpeningRefundId(refundId);
            try {
                onOpenDonation(await fetchRefundDonation(refundId));
            } catch (e) {
                console.error('Failed to load donation for drawer:', e);
                toast.error('Could not load donation');
            } finally {
                setOpeningRefundId(null);
            }
        },
        [onOpenDonation]
    );

    const count = data?.count ?? 0;
    const totals = data?.totals;

    const reportParams = new URLSearchParams();
    if (filters.from) reportParams.set('from_date', filters.from);
    if (filters.to) reportParams.set('to_date', filters.to);
    if (filters.status) reportParams.set('status', filters.status);
    const reportQuery = reportParams.toString();

    return (
        <RefundsTable
            rows={data?.results ?? []}
            count={count}
            page={page}
            setPage={setPage}
            sorting={sorting}
            onSortingChange={handleSortingChange}
            isLoading={isLoading}
            isError={isError}
            showCampaign
            showStatus
            onToggleCompletion={handleToggleCompletion}
            savingRefundId={savingRefundId}
            onOpenDonation={onOpenDonation ? handleOpenDonation : undefined}
            openingRefundId={openingRefundId}
            emptyMessage='No refunds match these filters.'
            errorMessage='Could not load refunds.'
            summary={
                <>
                    <p>
                        {count
                            ? `Showing ${Math.min(
                                  REFUNDS_PAGE_SIZE,
                                  count - (page - 1) * REFUNDS_PAGE_SIZE
                              )} of ${count} refunds`
                            : 'No refunds'}
                    </p>
                    <div className='flex items-center gap-4 text-[#949599]'>
                        {totals && (
                            <span>
                                {formatCurrency(
                                    Number(totals.total_refunded)
                                )}{' '}
                                processed
                                {totals.pending_count
                                    ? ` · ${totals.pending_count} pending`
                                    : ''}
                                {totals.failed_count
                                    ? ` · ${totals.failed_count} failed`
                                    : ''}
                            </span>
                        )}
                        <Link
                            to={`/reports/predefined/refund-audit/results${
                                reportQuery ? `?${reportQuery}` : ''
                            }`}
                            className='font-bold text-primary'
                        >
                            Refund audit report
                        </Link>
                    </div>
                </>
            }
        />
    );
};

export default AllRefundsTable;
